/**
 * AdjustPointsDialog
 * ─────────────────────────────────────────────────────────────
 * PIN-gated dialog to add or deduct loyalty points for a customer.
 * Used in two places:
 *   1) CRM → Loyalty Program (free-form search)
 *   2) CRM → Customer Profile → Loyalty tab (preset customer)
 *
 * Features:
 *   - Live debounced customer search (name, email, phone) via
 *     GET /api/admin/loyalty/customer-search?q=
 *   - Displays selected customer with name/email/phone and current balance
 *   - Add (+) or deduct (-) toggle + amount input + new-balance preview
 *   - Reason textarea (required)
 *   - Employee/admin 4-digit PIN (required)
 *   - Submits to POST /api/admin/loyalty/adjust which:
 *       - validates PIN, blocks insufficient balance,
 *       - logs ledger entry with kind=adjustment + employeeId + reason,
 *       - returns new balance.
 *   - Disables button while submitting (no double-submit)
 *   - Clears PIN on success
 *   - Calls onSuccess({ ledger_id, balance, adjusted, reason, by, customer })
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Search, Plus, Minus, Sparkles, User, Mail, Phone, Loader2, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdjustPointsDialog = ({ open, onOpenChange, presetCustomer = null, onSuccess }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null); // {user_id, name, email, phone, balance}
  const [direction, setDirection] = useState('add'); // 'add' | 'deduct'
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  // Reset state whenever dialog opens
  useEffect(() => {
    if (!open) return;
    setAmount(''); setReason(''); setPin(''); setSubmitting(false);
    setDirection('add');
    if (presetCustomer) {
      setSelected({
        user_id: presetCustomer.user_id,
        name: presetCustomer.name || '',
        email: presetCustomer.email || '',
        phone: presetCustomer.phone || '',
        balance: presetCustomer.balance ?? presetCustomer.loyalty_balance ?? 0,
      });
      setQuery('');
      setResults([]);
    } else {
      setSelected(null);
      setQuery('');
      setResults([]);
    }
  }, [open, presetCustomer]);

  // Debounced live search
  useEffect(() => {
    if (presetCustomer) return; // skip when a customer is locked in
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort?.();
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const r = await axios.get(
          `${API_URL}/api/admin/loyalty/customer-search`,
          { params: { q, limit: 8 }, signal: controller.signal, withCredentials: true }
        );
        setResults(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        if (!axios.isCancel?.(e) && e.name !== 'CanceledError') {
          /* eslint-disable no-console */
          console.warn('Loyalty search failed:', e);
        }
      } finally {
        setSearching(false);
      }
    }, 280);
    return () => clearTimeout(debounceRef.current);
  }, [query, presetCustomer]);

  const signedPoints = useMemo(() => {
    const n = parseInt(amount, 10);
    if (!n || isNaN(n) || n <= 0) return 0;
    return direction === 'add' ? n : -n;
  }, [amount, direction]);

  const newBalancePreview = useMemo(() => {
    if (!selected) return null;
    return Math.max(-9999999, selected.balance + signedPoints);
  }, [selected, signedPoints]);

  const insufficient = direction === 'deduct' && selected && newBalancePreview !== null && newBalancePreview < 0;

  const canSubmit = (
    !!selected &&
    Math.abs(signedPoints) > 0 &&
    reason.trim().length >= 3 &&
    /^\d{4}$/.test(pin) &&
    !submitting &&
    !insufficient
  );

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/loyalty/points`,
        {
          user_email: selected.email,
          points: signedPoints,
          reason: reason.trim(),
          pin,
        },
        { withCredentials: true }
      );
      toast.success(`Points updated! New balance: ${r.data.balance} pts`);
      // Clear sensitive fields
      setPin('');
      // Refresh the displayed balance
      setSelected((c) => c ? { ...c, balance: r.data.balance } : c);
      setAmount('');
      setReason('');
      onSuccess?.({
        ...r.data,
        customer: { ...selected, balance: r.data.balance },
      });
      // Close after a moment so the user sees the new balance
      setTimeout(() => onOpenChange?.(false), 600);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Adjustment failed';
      toast.error(msg);
      // Always clear the PIN on error too
      setPin('');
    } finally {
      setSubmitting(false);
    }
  };

  const clearSelected = () => {
    setSelected(null);
    setQuery('');
    setResults([]);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange?.(v)}>
      <DialogContent className="max-w-md bg-white" data-testid="adjust-points-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Fredoka' }}>Adjust customer points</DialogTitle>
          <DialogDescription className="text-xs">
            Credit (+) or debit (-) loyalty points. Reason and 4-digit PIN required for audit.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* ── Customer search / display ── */}
          {!selected ? (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">Find customer</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B7A]" />
                <Input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Name, email, or phone…"
                  className="pl-10 rounded-xl"
                  data-testid="adj-search"
                />
                {searching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B7A] animate-spin" />
                )}
              </div>
              <p className="text-[11px] text-[#6B6B7A] mt-1">
                Type at least 2 characters. Searches name, email, or phone.
              </p>
              {results.length > 0 && (
                <ul
                  className="mt-2 border border-[#E8E5DF] rounded-xl bg-white max-h-60 overflow-y-auto divide-y divide-[#E8E5DF]"
                  data-testid="adj-search-results"
                >
                  {results.map((c) => (
                    <li key={c.user_id}>
                      <button
                        type="button"
                        onClick={() => setSelected(c)}
                        className="w-full text-left px-3 py-2 hover:bg-[#FFFDF8] flex items-center justify-between gap-2 transition"
                        data-testid={`adj-result-${c.user_id}`}
                      >
                        <div className="min-w-0">
                          <p className="font-medium text-sm text-[#1E1E24] truncate">{c.name || '(no name)'}</p>
                          <p className="text-xs text-[#6B6B7A] truncate">{c.email}{c.phone ? ` · ${c.phone}` : ''}</p>
                        </div>
                        <span className="text-xs font-bold text-[#FF3B6B] shrink-0">{c.balance} pts</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {query.trim().length >= 2 && !searching && results.length === 0 && (
                <p className="text-xs text-[#6B6B7A] italic mt-2" data-testid="adj-no-results">
                  No matches found.
                </p>
              )}
            </div>
          ) : (
            <div
              className="bg-gradient-to-br from-[#FF3B6B]/5 to-[#FB923C]/5 border border-[#FF3B6B]/30 rounded-xl p-3 flex items-start gap-3"
              data-testid="adj-selected-customer"
            >
              <div className="w-10 h-10 rounded-full bg-[#FF3B6B]/10 text-[#FF3B6B] flex items-center justify-center shrink-0">
                <User className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm text-[#1E1E24] truncate" data-testid="adj-sel-name">
                  {selected.name || '(no name)'}
                </p>
                <div className="flex items-center gap-1 text-xs text-[#6B6B7A] truncate">
                  <Mail className="w-3 h-3 shrink-0" />
                  <span className="truncate" data-testid="adj-sel-email">{selected.email}</span>
                </div>
                {selected.phone && (
                  <div className="flex items-center gap-1 text-xs text-[#6B6B7A]">
                    <Phone className="w-3 h-3" />
                    <span>{selected.phone}</span>
                  </div>
                )}
                <p className="mt-1 text-xs">
                  Current balance:{' '}
                  <span className="font-bold text-[#FF3B6B]" data-testid="adj-sel-balance">
                    {selected.balance.toLocaleString()} pts
                  </span>
                </p>
              </div>
              {!presetCustomer && (
                <button
                  type="button"
                  onClick={clearSelected}
                  className="text-[#6B6B7A] hover:text-[#FF3B6B] shrink-0"
                  title="Pick a different customer"
                  data-testid="adj-clear-customer"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}

          {/* ── Adjustment fields (visible after customer selected) ── */}
          {selected && (
            <>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">Adjustment</Label>
                <div className="flex gap-2 mt-1">
                  <button
                    type="button"
                    onClick={() => setDirection('add')}
                    className={`flex-1 h-10 rounded-xl border-2 font-semibold text-sm flex items-center justify-center gap-1 transition ${
                      direction === 'add' ? 'border-[#06D6A0] bg-[#06D6A0]/10 text-[#06D6A0]' : 'border-[#E8E5DF] text-[#6B6B7A] hover:border-[#06D6A0]/50'
                    }`}
                    data-testid="adj-dir-add"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add
                  </button>
                  <button
                    type="button"
                    onClick={() => setDirection('deduct')}
                    className={`flex-1 h-10 rounded-xl border-2 font-semibold text-sm flex items-center justify-center gap-1 transition ${
                      direction === 'deduct' ? 'border-[#FF3B6B] bg-[#FF3B6B]/10 text-[#FF3B6B]' : 'border-[#E8E5DF] text-[#6B6B7A] hover:border-[#FF3B6B]/50'
                    }`}
                    data-testid="adj-dir-deduct"
                  >
                    <Minus className="w-3.5 h-3.5" /> Deduct
                  </button>
                </div>
                <div className="relative mt-2">
                  <Input
                    type="number"
                    min="1"
                    inputMode="numeric"
                    placeholder="Points"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, '').slice(0, 7))}
                    className="rounded-xl pr-16"
                    data-testid="adj-amount"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#6B6B7A] font-semibold">pts</span>
                </div>
                {Math.abs(signedPoints) > 0 && (
                  <div className={`mt-2 text-xs flex items-center gap-1 ${insufficient ? 'text-[#FF3B6B]' : 'text-[#6B6B7A]'}`}>
                    {insufficient ? (
                      <>
                        <AlertTriangle className="w-3 h-3" />
                        Cannot deduct {Math.abs(signedPoints).toLocaleString()} pts — only {selected.balance.toLocaleString()} available.
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3 text-[#FB923C]" />
                        New balance: <b className="text-[#1E1E24]" data-testid="adj-new-balance">{newBalancePreview.toLocaleString()} pts</b>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">
                  Reason <span className="text-[#FF3B6B]">*</span>
                </Label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value.slice(0, 200))}
                  rows={2}
                  placeholder="e.g. Compensation for delayed party, manager comp, etc."
                  className="mt-1 rounded-xl text-sm"
                  data-testid="adj-reason"
                />
                <p className="text-[10px] text-[#6B6B7A] mt-0.5">{reason.length}/200 · min 3 characters</p>
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">
                  Your 4-digit PIN <span className="text-[#FF3B6B]">*</span>
                </Label>
                <Input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="••••"
                  className="mt-1 rounded-xl text-center font-mono tracking-[0.5em] text-lg"
                  data-testid="adj-pin"
                />
                <p className="text-[10px] text-[#6B6B7A] mt-0.5">Validated on the server. Used to log who made the adjustment.</p>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={submitting}
            className="rounded-full"
            data-testid="adj-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white min-w-[140px]"
            data-testid="adj-submit"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Applying…</>
            ) : signedPoints !== 0 ? (
              <><CheckCircle2 className="w-4 h-4 mr-1" /> Apply {signedPoints > 0 ? '+' : ''}{signedPoints} pts</>
            ) : (
              'Apply'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdjustPointsDialog;
